import dayjs from "dayjs";
import { Fund } from "types/types";
import { CUSTOM_PERIOD_VALUE, useReportingPeriod } from "./hooks";
import {
  Card,
  CustomPeriodField,
  CustomPeriodSection,
  DateInput,
  DesktopFundsList,
  DropdownWrapper,
  EndDateValue,
  FundCard,
  FundName,
  HelperMessage,
  HelpTooltip,
  InceptionDate,
  Label,
  MobileFundsList,
  ReportingPeriod,
  Select,
  Table,
  TBody,
  Td,
  Th,
  THead,
  Tr,
  ValidationMessage,
} from "./styles";

type DropdownOptions = {
  label: string;
  value: string;
};

const formatReportingPeriod = (value: number): string =>
  value === 1 ? `${value} year` : `${value} years`;

const getReportingPeriodOptions = (value: number): Array<DropdownOptions> => {
  const dropdownOptions: Array<DropdownOptions> = [];
  for (let i = value; i >= 1; i--) {
    dropdownOptions.push({
      label: `${formatReportingPeriod(i)}${i === value ? " (max)" : ""}`,
      value: i.toString(),
    });
  }
  return dropdownOptions;
};

function ReportingPeriodCard() {
  const {
    selectedFunds,
    maximumReportingPeriod,
    reportingPeriod,
    isCustomPeriod,
    customStartDate,
    customStartDateValid,
    minStartDate,
    maxStartDate,
    endDate,
    handleReportingPeriodChange,
    handleSelectCustomPeriod,
    handleCustomStartDateChange,
    handleTooltip,
  } = useReportingPeriod();

  const funds = selectedFunds.filter((fund) => !!fund.allocation);

  return (
    <Card>
      <DesktopFundsList>
        <Table>
          <THead>
            <Tr>
              <Th>Investment fund</Th>

              <Th style={{ width: "170px" }}>Fund inception</Th>

              <Th style={{ width: "170px" }}>Available data</Th>
            </Tr>
          </THead>

          <TBody>
            {funds.map(
              ({ name, inceptionDate, availableReportingPeriod }: Fund) => (
                <Tr key={name}>
                  <Td>{name}</Td>
                  <Td>{dayjs(inceptionDate).format("MMM YYYY")}</Td>
                  <Td>{formatReportingPeriod(availableReportingPeriod)}</Td>
                </Tr>
              ),
            )}
          </TBody>
        </Table>
      </DesktopFundsList>

      <MobileFundsList>
        {funds.map(
          ({ name, inceptionDate, availableReportingPeriod }: Fund) => (
            <FundCard key={name}>
              <FundName>{name}</FundName>
              <InceptionDate>
                <strong>Inception date:</strong>{" "}
                {dayjs(inceptionDate).format("MMM YYYY")}
              </InceptionDate>
              <ReportingPeriod>
                <strong>Reporting period:</strong>{" "}
                {formatReportingPeriod(availableReportingPeriod)}
              </ReportingPeriod>
            </FundCard>
          ),
        )}
      </MobileFundsList>

      <Label htmlFor="reportingPeriod">Select the reporting period below</Label>

      <DropdownWrapper>
        <Select
          id="reportingPeriod"
          value={isCustomPeriod ? CUSTOM_PERIOD_VALUE : reportingPeriod}
          onChange={(e) => {
            if (e.target.value === CUSTOM_PERIOD_VALUE) {
              handleSelectCustomPeriod();
            } else {
              handleReportingPeriodChange(parseInt(e.target.value));
            }
          }}
        >
          {[
            ...getReportingPeriodOptions(maximumReportingPeriod).map(
              ({ label, value }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ),
            ),
            <option key={CUSTOM_PERIOD_VALUE} value={CUSTOM_PERIOD_VALUE}>
              Custom start date
            </option>,
          ]}
        </Select>

        <HelpTooltip onClick={handleTooltip}>
          The fund with the shortest available data period in your portfolio
          dictates the maximum reporting period available.
        </HelpTooltip>
      </DropdownWrapper>

      {isCustomPeriod && (
        <CustomPeriodSection>
          {customStartDate && !customStartDateValid ? (
            <ValidationMessage>
              Start date must be between{" "}
              {dayjs(minStartDate).format("DD/MM/YYYY")} and{" "}
              {dayjs(maxStartDate).format("DD/MM/YYYY")}
            </ValidationMessage>
          ) : (
            <HelperMessage>
              Choose a start date between{" "}
              {dayjs(minStartDate).format("DD/MM/YYYY")} and{" "}
              {dayjs(maxStartDate).format("DD/MM/YYYY")}
            </HelperMessage>
          )}

          <CustomPeriodField>
            <Label htmlFor="customStartDate">Start date</Label>
            <DateInput
              id="customStartDate"
              type="date"
              value={customStartDate ?? ""}
              onChange={(e) => handleCustomStartDateChange(e.target.value)}
            />
          </CustomPeriodField>

          <CustomPeriodField>
            <Label as="span">End date</Label>
            <EndDateValue>{dayjs(endDate).format("DD/MM/YYYY")}</EndDateValue>
          </CustomPeriodField>
        </CustomPeriodSection>
      )}
    </Card>
  );
}

export default ReportingPeriodCard;
